import React, { useState, useMemo } from 'react';
import { VisitedProvider } from './VisitedContext';
import { setLocalStorage, getLocalStorage } from '../../utils/localStorage';
import { LOCALSTORAGE_VISITED_KEY } from '../../constants/index';
import get from 'lodash/get';

/**
 * A provider component that manages and persists a list of visited items in local storage.
 *
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - The child components that will have access to the visited context.
 * @returns {JSX.Element} The VisitedContainer component.
 */

function VisitedContainer({ children }) {
  const [visited, setVisited] = useState(JSON.parse(getLocalStorage(LOCALSTORAGE_VISITED_KEY) || '[]'));

  /**
  * Updates the visited list and persists it in local storage.
  *
  * @param {Array} values - The updated visited items array.
  */
  const update = (values) => {
    setLocalStorage(LOCALSTORAGE_VISITED_KEY, JSON.stringify(values));
    setVisited(values);
  }

  /**
 * Checks if a specific item is in the visited list.
 *
 * @param {Object} visitedItem - The item to check.
 * @param {Array} [arrayToCheckFrom=visited] - The array to check from (default is the visited list).
 * @returns {boolean} True if the item exists in the visited list, false otherwise.
 */
  const isVisited = (visitedItem, arrayToCheckFrom = visited) => {
    const isItemInVisited = (itemToCheck) => arrayToCheckFrom.some((visitedItem) => get(visitedItem, 'id', '1') == get(itemToCheck, 'id', '2'))

    return Array.isArray(visited) && isItemInVisited(visitedItem);
  }

  /**
 * Adds a new item to the visited list if it's not already present.
 *
 * @param {Object} newVisitedItem - The item to add.
 */
  const addToVisited = (newVisitedItem, callBack = () => { }) => {
    if (typeof newVisitedItem !== 'object' || !newVisitedItem) return
    if (!Array.isArray(visited) || isVisited(newVisitedItem)) return;

    update([{ ...newVisitedItem, visitedAt: new Date().toISOString() }, ...visited]);
    callBack([{ ...newVisitedItem, visitedAt: new Date().toISOString() }, ...visited])
  }

  /**
 * Removes an item from the visited list.
 *
 * @param {Object} visitedItemToRemove - The item to remove.
 */
  const removeFromVisited = (visitedItemToRemove) => {
    if (typeof visitedItemToRemove !== 'object' || !visitedItemToRemove) return
    if (!Array.isArray(visited) || !(isVisited(visitedItemToRemove))) return;

    update(visited.filter((visitedItem) => get(visitedItem, 'id', null) !== get(visitedItemToRemove, 'id', null)));
  }

  /** Context value containing visited items and related functions */
  const contextValue = useMemo(() => ({
    visited,
    addToVisited,
    removeFromVisited,
    isVisited,
  }), [visited]);

  return <VisitedProvider value={contextValue}>{children}</VisitedProvider>;
}

export default VisitedContainer;
